import React from 'react';
import { useNavigate } from 'react-router-dom';

import './FloorPlanMap.css';

import type { EventDto } from '../../api/types';
import { groupEventsByResourceForDate } from '../../domain/floorPlan/eventsByResource';
import type { ResourceState, UserResourceStatus } from '../../domain/floorPlan/resourceState';
import { computeResourceState, computeUserResourceStatus } from '../../domain/floorPlan/resourceState';
import { type TableIndicator, computeTableIndicator } from '../../domain/floorPlan/tableIndicator';
import {
  DEFAULT_VIP_LAYOUT_MODE,
  RESOURCES,
  VIP_COMBINED_RESOURCE_ID,
  VIP_SPLIT_RESOURCE_IDS,
  type VipLayoutMode,
  getResourceLabel,
  normalizeResourceId,
} from '../../resources/resources';
import { useAppSelector } from '../../store/hooks';
import { BowCircleIcon } from '../../ui/icons/BowCircleIcon';
import { NatureCircleIcon } from '../../ui/icons/NatureCircleIcon';
import { PingPongTableIcon } from '../../ui/icons/PingPongTableIcon';
import { SwordsCircleIcon } from '../../ui/icons/SwordsCircleIcon';
import { TableRectangularIcon } from '../../ui/icons/TableRectangularIcon';
import { TableSmallIcon } from '../../ui/icons/TableSmallIcon';
import { formatDateYYYYMMDD } from '../../utils/date';

import { GUEST_OPEN_ICON_STYLE, TABLE_TONE_TEXT } from './tableToneColors';

function userStatusColor(status: UserResourceStatus): string {
  if (status === 'yellow') return 'var(--color-warning-500)';
  if (status === 'green') return 'var(--color-success-500)';
  return 'var(--color-wood-600)';
}

type FloorPlanMapProps = {
  selectedDate: Date;
  events: EventDto[];
  /** Tables an admin has opened for guests on the selected date; drawn green. */
  guestOpenResourceIds?: ReadonlySet<string>;
};

type ResourceShape = 'table' | 'table-small' | 'ping-pong' | 'swords' | 'bow' | 'nature' | 'vip-combined' | 'courtyard';

type ResourceNode = {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  shape: ResourceShape;
};

// Recolors the brown table icon green; the card background stays unchanged.
const VIP_LAYOUT_STORAGE_KEY = 'ttb_vip_layout_mode';
const VIP_SWITCH_TO_COMBINED_LABEL = '> <';
const VIP_SWITCH_TO_SPLIT_LABEL = '< >';
const COURTYARD_RESOURCE_ID = 'zone_courtyard_1';
const COURTYARD_TITLE_MAX_LINES = 6;
const COURTYARD_TITLE_MAX_CHARS_PER_LINE = 12;

function splitCourtyardTitleToLines(title: string): string[] {
  const words = title.split(/\s+/).filter(Boolean);
  if (!words.length) return [];

  const lines: string[] = [];

  const pushWordAsChunks = (word: string) => {
    let rest = word;
    while (rest.length > COURTYARD_TITLE_MAX_CHARS_PER_LINE) {
      lines.push(rest.slice(0, COURTYARD_TITLE_MAX_CHARS_PER_LINE));
      rest = rest.slice(COURTYARD_TITLE_MAX_CHARS_PER_LINE);
      if (lines.length >= COURTYARD_TITLE_MAX_LINES) return '';
    }
    return rest;
  };

  for (const originalWord of words) {
    if (lines.length >= COURTYARD_TITLE_MAX_LINES) break;
    const word = pushWordAsChunks(originalWord);
    if (!word) break;

    if (!lines.length) {
      lines.push(word);
      continue;
    }

    const lastLineIndex = lines.length - 1;
    const lastLine = lines[lastLineIndex];
    const candidate = `${lastLine} ${word}`;
    if (candidate.length <= COURTYARD_TITLE_MAX_CHARS_PER_LINE) {
      lines[lastLineIndex] = candidate;
      continue;
    }

    lines.push(word);
  }

  if (lines.length > COURTYARD_TITLE_MAX_LINES) {
    lines.length = COURTYARD_TITLE_MAX_LINES;
  }

  const joinedOriginal = words.join(' ');
  const joinedLines = lines.join(' ');
  if (joinedOriginal.length > joinedLines.length && lines.length) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] =
      last.length >= COURTYARD_TITLE_MAX_CHARS_PER_LINE
        ? `${last.slice(0, COURTYARD_TITLE_MAX_CHARS_PER_LINE - 1)}…`
        : `${last}…`;
  }

  return lines;
}

const RESOURCE_NODES: ResourceNode[] = [
  { id: 'activity_fencing_1', x: 24, y: 45, w: 55, h: 60, shape: 'swords' },
  { id: 'activity_archery_1', x: 90, y: 45, w: 55, h: 60, shape: 'bow' },
  { id: 'activity_hiking_1', x: 156, y: 45, w: 55, h: 60, shape: 'nature' },
  { id: 'table_game_5', x: 250, y: 48, w: 66, h: 46, shape: 'table' },
  { id: 'table_game_6', x: 330, y: 48, w: 66, h: 46, shape: 'table' },
  { id: 'table_game_7', x: 280, y: 48, w: 90, h: 46, shape: 'vip-combined' },
  { id: 'table_game_4', x: 30, y: 122, w: 66, h: 46, shape: 'table' },
  { id: 'table_ping_pong_1', x: 30, y: 188, w: 66, h: 75, shape: 'ping-pong' },
  { id: 'zone_courtyard_1', x: 115, y: 145, w: 85, h: 118, shape: 'courtyard' },
  { id: 'table_game_1', x: 250, y: 158, w: 66, h: 46, shape: 'table' },
  { id: 'table_game_2', x: 330, y: 158, w: 66, h: 46, shape: 'table' },
  { id: 'table_game_3', x: 330, y: 220, w: 66, h: 46, shape: 'table' },
];

export function FloorPlanMap({ selectedDate, events, guestOpenResourceIds }: FloorPlanMapProps) {
  const navigate = useNavigate();
  const telegramId = useAppSelector((s) => s.auth.user?.telegramId);
  const [vipLayoutMode, setVipLayoutMode] = React.useState<VipLayoutMode>(() => {
    try {
      const saved = window.localStorage.getItem(VIP_LAYOUT_STORAGE_KEY);
      return saved === 'combined' ? 'combined' : DEFAULT_VIP_LAYOUT_MODE;
    } catch {
      return DEFAULT_VIP_LAYOUT_MODE;
    }
  });

  const dateKey = formatDateYYYYMMDD(selectedDate);

  const eventsByResource = React.useMemo(() => groupEventsByResourceForDate(events, dateKey), [dateKey, events]);

  const normalizedEventsByResource = React.useMemo(() => {
    const normalized = new Map<string, EventDto[]>();
    for (const [resourceId, list] of eventsByResource.entries()) {
      const normalizedId = normalizeResourceId(resourceId);
      const current = normalized.get(normalizedId) ?? [];
      normalized.set(normalizedId, current.concat(list));
    }
    return normalized;
  }, [eventsByResource]);

  const resourceStates = React.useMemo(() => {
    const states = new Map<string, ResourceState>();
    for (const r of RESOURCES) {
      const list = normalizedEventsByResource.get(r.id) ?? [];
      states.set(r.id, computeResourceState(list));
    }
    return states;
  }, [normalizedEventsByResource]);

  const userStatuses = React.useMemo(() => {
    const statuses = new Map<string, UserResourceStatus>();
    for (const r of RESOURCES) {
      const list = normalizedEventsByResource.get(r.id) ?? [];
      statuses.set(r.id, computeUserResourceStatus(list, telegramId));
    }
    return statuses;
  }, [normalizedEventsByResource, telegramId]);

  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(timerId);
  }, []);

  const tableIndicators = React.useMemo(() => {
    const indicators = new Map<string, TableIndicator | null>();
    for (const r of RESOURCES) {
      const list = normalizedEventsByResource.get(r.id) ?? [];
      indicators.set(r.id, computeTableIndicator(list, nowMs));
    }
    return indicators;
  }, [normalizedEventsByResource, nowMs]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(VIP_LAYOUT_STORAGE_KEY, vipLayoutMode);
    } catch {
      // Ignore storage failures in constrained webviews.
    }
  }, [vipLayoutMode]);

  const vipHasAnyBookings = React.useMemo(() => {
    const vipIds = [...VIP_SPLIT_RESOURCE_IDS, VIP_COMBINED_RESOURCE_ID];
    return vipIds.some((resourceId) => (normalizedEventsByResource.get(resourceId) ?? []).length > 0);
  }, [normalizedEventsByResource]);

  const vipHasCombinedBookings = (normalizedEventsByResource.get(VIP_COMBINED_RESOURCE_ID) ?? []).length > 0;
  const vipHasSplitBookings = [...VIP_SPLIT_RESOURCE_IDS].some(
    (resourceId) => (normalizedEventsByResource.get(resourceId) ?? []).length > 0,
  );
  const effectiveVipLayoutMode: VipLayoutMode = vipHasCombinedBookings
    ? 'combined'
    : vipHasSplitBookings
      ? 'split'
      : vipLayoutMode;

  const isVipCombinedMode = effectiveVipLayoutMode === 'combined';
  const visibleNodes = React.useMemo(() => {
    return RESOURCE_NODES.filter((node) => {
      if (isVipCombinedMode) return !VIP_SPLIT_RESOURCE_IDS.has(node.id);
      return node.id !== VIP_COMBINED_RESOURCE_ID;
    });
  }, [isVipCombinedMode]);

  const courtyardEventTitleLines = React.useMemo(() => {
    const courtyardEvents = normalizedEventsByResource.get(COURTYARD_RESOURCE_ID) ?? [];
    const nowMs = Date.now();

    const firstActualEvent = courtyardEvents.find((event) => {
      const endAtMs = new Date(event.endAt).getTime();
      return Number.isFinite(endAtMs) && endAtMs >= nowMs;
    });

    if (!firstActualEvent?.title) return [];
    const title = firstActualEvent.title.trim();
    if (!title) return [];
    return splitCourtyardTitleToLines(title);
  }, [normalizedEventsByResource]);

  const handleSwitchVipLayout = React.useCallback(
    (mode: VipLayoutMode) => {
      if (vipHasAnyBookings) return;
      setVipLayoutMode(mode);
    },
    [vipHasAnyBookings],
  );

  return (
    <div>
      <div className="ttbFloorPlanFrame">
        <svg className="ttbFloorPlanSvg" viewBox="0 0 430 300" preserveAspectRatio="xMidYMid meet">
          <rect
            x="8"
            y="16"
            width="414"
            height="272"
            rx="12"
            fill="rgba(245, 239, 231, 0.2)"
            stroke="var(--color-wood-600)"
            strokeWidth="2"
          />
          <rect x="18" y="24" width="198" height="256" rx="10" fill="none" stroke="var(--color-wood-600)" strokeWidth="2" />
          <rect x="226" y="24" width="188" height="116" rx="10" fill="none" stroke="var(--color-wood-600)" strokeWidth="2" />
          <rect x="226" y="152" width="188" height="128" rx="10" fill="none" stroke="var(--color-wood-600)" strokeWidth="2" />

          <rect x="16" y="24" width="204" height="88" rx="8" fill="none" stroke="var(--color-wood-500)" strokeWidth="1.5" />
          <rect x="234" y="32" width="180" height="96" rx="8" fill="none" stroke="var(--color-wood-500)" strokeWidth="1.5" />
          <rect x="234" y="160" width="180" height="112" rx="8" fill="none" stroke="var(--color-wood-500)" strokeWidth="1.5" />

          <g>
            <rect x="214" y="70" width="12" height="28" rx="2" fill="#8D6E63" stroke="#4E342E" strokeWidth="1" />
            <rect x="214" y="194" width="12" height="28" rx="2" fill="#8D6E63" stroke="#4E342E" strokeWidth="1" />
          </g>

          <text x="118" y="40" className="ttbFloorPlanTextLabel ttbFloorPlanTextLabelStrong" textAnchor="middle">Походы</text>
          <text x="324" y="134" className="ttbFloorPlanTextLabel ttbFloorPlanTextLabelStrong" textAnchor="middle">VIP</text>
          <text x="280" y="272" className="ttbFloorPlanTextLabel ttbFloorPlanTextLabelStrong" textAnchor="middle">Таверна</text>        

          <g transform="translate(357, 116)">
            <g
              role="button"
              tabIndex={vipHasAnyBookings ? -1 : 0}
              aria-disabled={vipHasAnyBookings}
              onClick={() => handleSwitchVipLayout(isVipCombinedMode ? 'split' : 'combined')}
              onKeyDown={(event) => {
                if (vipHasAnyBookings) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSwitchVipLayout(isVipCombinedMode ? 'split' : 'combined');
                }
              }}
              className={`ttbVipLayoutSwitch ttbVipLayoutSwitchActive ${
                vipHasAnyBookings ? 'ttbVipLayoutSwitchDisabled' : ''
              }`}
            >
              <rect x="0" y="0" width="40" height="18" rx="4" />
              <text x="20" y="13" textAnchor="middle" className="ttbVipLayoutSwitchText">
                {isVipCombinedMode ? VIP_SWITCH_TO_SPLIT_LABEL : VIP_SWITCH_TO_COMBINED_LABEL}
              </text>
            </g>
          </g>

          {visibleNodes.map((node) => {
            const state = resourceStates.get(node.id) ?? { bookingsCount: 0, status: 'green' as const };
            const userStatus = userStatuses.get(node.id) ?? 'brown';
            const hasTableIndicator =
              node.shape === 'table' ||
              node.shape === 'table-small' ||
              node.shape === 'vip-combined' ||
              node.shape === 'ping-pong';
            const indicator = hasTableIndicator ? (tableIndicators.get(node.id) ?? null) : null;
            const tone = indicator?.tone ?? 'default';
            const iconStyle = guestOpenResourceIds?.has(node.id) ? GUEST_OPEN_ICON_STYLE : undefined;
            // Стол сдвигается влево, чтобы справа поместилась дробь «занято/всего»
            const iconShift = indicator && node.shape !== 'ping-pong' ? -8 : 0;
            const countLabel = hasTableIndicator
              ? indicator
                ? `${indicator.occupied}/${indicator.capacity}`
                : ''
              : String(state.bookingsCount);

            return (
              <g
                key={node.id}
                onClick={() => navigate(`/floor-plan/${encodeURIComponent(node.id)}`)}
                style={{ cursor: 'pointer' }}
              >
                <rect
                  x={node.x}
                  y={node.y}
                  width={node.w}
                  height={node.h}
                  rx="10"
                  fill="rgba(245, 239, 231, 0.9)"
                  stroke={userStatusColor(userStatus)}
                  strokeWidth="3"
                />

                {node.shape === 'table' && (
                  <g transform={`translate(${node.x + node.w / 2 - 20 + iconShift}, ${node.y + 4})`} style={iconStyle}>
                    <TableRectangularIcon width="40" height="40" />
                  </g>
                )}
                {node.shape === 'table-small' && (
                  <g transform={`translate(${node.x + node.w / 2 - 16 + iconShift}, ${node.y + 2})`} style={iconStyle}>
                    <TableSmallIcon width="32" height="32" />
                  </g>
                )}
                {node.shape === 'ping-pong' && (
                  <g transform={`translate(${node.x + node.w / 2 - 20}, ${node.y + 8})`}>
                    <PingPongTableIcon width="40" height="40" />
                  </g>
                )}
                {node.shape === 'swords' && (
                  <g transform={`translate(${node.x + node.w / 2 - 20}, ${node.y + 6})`}>
                    <SwordsCircleIcon width="40" height="40" />
                  </g>
                )}
                {node.shape === 'bow' && (
                  <g transform={`translate(${node.x + node.w / 2 - 20}, ${node.y + 6})`}>
                    <BowCircleIcon width="40" height="40" />
                  </g>
                )}
                {node.shape === 'nature' && (
                  <g transform={`translate(${node.x + node.w / 2 - 20}, ${node.y + 6})`}>
                    <NatureCircleIcon width="40" height="40" />
                  </g>
                )}
                {node.shape === 'vip-combined' && (
                  <>
                    <g transform={`translate(${node.x + node.w / 2 - 40 + iconShift}, ${node.y + 4})`} style={iconStyle}>
                      <TableRectangularIcon width="40" height="40" />
                    </g>
                    <g transform={`translate(${node.x + node.w / 2 - 14 + iconShift}, ${node.y + 4})`} style={iconStyle}>
                      <TableRectangularIcon width="40" height="40" />
                    </g>
                  </>
                )}
                {node.shape === 'courtyard' &&
                  (courtyardEventTitleLines.length > 0 ? (
                    <>
                      <defs>
                        <clipPath id={`courtyard-text-clip-${node.id}`}>
                          <rect x={node.x + 4} y={node.y + 6} width={node.w - 8} height={node.h - 12} rx="8" />
                        </clipPath>
                      </defs>
                      <g clipPath={`url(#courtyard-text-clip-${node.id})`}>
                        <text
                          x={node.x + node.w / 2}
                          y={node.y + node.h / 2 - ((courtyardEventTitleLines.length - 1) * 6)}
                          className="ttbCourtyardEventTitle"
                          fontSize="12"
                          textAnchor="middle"
                        >
                          {courtyardEventTitleLines.map((line, lineIndex) => (
                            <tspan
                              key={`${node.id}-title-line-${lineIndex}`}
                              x={node.x + node.w / 2}
                              dy={lineIndex === 0 ? 0 : 12}
                            >
                              {line}
                            </tspan>
                          ))}
                        </text>
                      </g>
                    </>
                  ) : (
                    <text
                      x={node.x + node.w / 2}
                      y={node.y + node.h / 2 + 6}
                      fontSize="28"
                      textAnchor="middle"
                    >
                      🏡
                    </text>
                  ))}

                <text
                  x={
                    node.shape === 'vip-combined'
                      ? node.x + node.w - 8
                      : node.shape === 'table' || node.shape === 'table-small'
                      ? node.x + node.w - 8
                      : node.x + node.w / 2
                  }
                  y={
                    node.shape === 'vip-combined' || node.shape === 'table' || node.shape === 'table-small'
                      ? node.y + node.h / 2 + 5
                      : node.shape === 'swords' || node.shape === 'bow' || node.shape === 'nature'
                      ? node.y + node.h - 4
                      : node.y + node.h - 10
                  }
                  fontSize={node.shape === 'vip-combined' ? 11 : hasTableIndicator ? 12 : 16}
                  fontWeight="800"
                  textAnchor={
                    node.shape === 'vip-combined'
                      ? 'end'
                      : node.shape === 'table' || node.shape === 'table-small'
                      ? 'end'
                      : 'middle'
                  }
                  fill={TABLE_TONE_TEXT[tone]}
                >
                  {countLabel}
                </text>
              </g>
            );
          })}

          {visibleNodes
            .filter((node) =>
              node.id !== 'activity_fencing_1' &&
              node.id !== 'activity_archery_1' &&
              node.id !== 'activity_hiking_1'
            )
            .map((node) => (
            <text
              key={`label-${node.id}`}
              x={node.x + node.w / 2}
              y={node.y + node.h + 12}
              fontSize="10"
              fontWeight="600"
              textAnchor="middle"
              fill="var(--color-wood-700)"
            >
              {getResourceLabel(node.id)}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}
